import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { CSSProperties } from 'styled-components';

import { TinderCardSwipe } from '../TinderCardSwipe';
import { PackTear } from './PackTear';
import type { RevealPackCards_anyCardInterface } from './__generated__/index.graphql';

const Root = styled(animated.div)`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: var(--double-unit);
  overflow: hidden;
`;
const TinderCardSwipeWrapper = styled.div`
  width: 100%;
  padding: var(--double-unit);
  position: absolute;
  inset: 0;
`;
const PackTearWrapper = styled.div`
  width: 100%;
  position: relative;

  &.gone {
    z-index: -1;
    opacity: 0;
  }
`;

type Props = {
  cards: RevealPackCards_anyCardInterface[];
  onContinue: () => void;
};
export const RevealPackCards = ({ cards, onContinue }: Props) => {
  const [packOpened, setPackOpened] = useState(false);
  const [packGone, setPackGone] = useState(false);

  const [tearAnimationSpring, tearAnimationSpringApi] = useSpring(() => ({
    '--progress': 0,
    '--progressPercentage': '0%',
  }));
  const animateProgress = (progress: number) => {
    tearAnimationSpringApi.start({
      '--progress': progress,
      '--progressPercentage': `${progress}%`,
    });
  };

  return (
    <Root style={tearAnimationSpring as CSSProperties}>
      <TinderCardSwipeWrapper className={classNames({ behind: !packGone })}>
        <TinderCardSwipe
          cards={cards}
          packOpened={packOpened}
          onContinue={onContinue}
        />
      </TinderCardSwipeWrapper>
      <PackTearWrapper className={classNames({ gone: packGone })}>
        <PackTear
          animateProgress={animateProgress}
          packOpened={packOpened}
          setPackOpened={setPackOpened}
          onPackGone={() => {
            setPackGone(true);
          }}
        />
      </PackTearWrapper>
    </Root>
  );
};

RevealPackCards.fragments = {
  anyCardInterface: gql`
    fragment RevealPackCards_anyCardInterface on AnyCardInterface {
      slug
      ...TinderCardSwipe_anyCardInterface
    }
    ${TinderCardSwipe.fragments.anyCardInterface}
  ` as TypedDocumentNode<RevealPackCards_anyCardInterface>,
};
